/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Lightbox } from '@nexigendigital/nxui'
import { useUpdatePasswordMutation } from 'api/account'
import { ChangePassword } from 'models/account'

import ChangePasswordForm from 'containers/account/form/changePasswordForm'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangePasswordLightbox({ status, close }: Props) {
    const formRef = useRef<FormikProps<ChangePassword>>(null)
    const [, { isLoading, isSuccess, isError, reset }] = useUpdatePasswordMutation({ fixedCacheKey: 'change-password' })

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (isError && formRef.current) {
            formRef.current.setSubmitting(false)
            formRef.current.resetForm()
        }
    }, [isError])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    if (isSuccess) {
        return (
            <Lightbox
                title={'Password Changed'}
                type={'success'}
                icon={'success'}
                description={'You can now see this reflected on the Account page.'}
                conditions={{
                    state: status,
                    action: () => {
                        close()
                        setTimeout(() => {
                            reset()
                        }, 500)
                    }
                }}
                actions={[{
                    label: 'Back to Account',
                    color: 'primary',
                    func: () => {
                        close()
                        setTimeout(() => {
                            reset()
                        }, 500)
                    }
                }]}
            />
        )
    }

    return (
        <Lightbox
            title={'Change Password'}
            content={<ChangePasswordForm form={formRef}/>}
            conditions={{ state: status, action: () => close() }}
            actions={[
                {
                    label: 'Cancel',
                    color: 'secondary',
                    func: () => close()
                },
                {
                    label: 'Change',
                    color: 'primary',
                    type: 'submit',
                    form: 'changePasswordForm',
                    loading: isLoading ? 'Changing' : false
                }
            ]}
        />
    )
}
